type Agent = {
  title: string
  desc: string
  bullets: string[]
}

const agents: Agent[] = [
  {
    title: 'Quote Agent',
    desc: 'Normalizes external quotes into one model with worst-case bounds.',
    bullets: ['Jupiter / deBridge / Mayan / CCTP', 'Worst-case + feasibility flags', 'Provider-agnostic inputs/outputs'],
  },
  {
    title: 'Route Agent',
    desc: 'Selects a safe execution path under user guardrails.',
    bullets: ['Exact-amount semantics first', 'Fallback routes with clear triggers', 'Deterministic plan output'],
  },
  {
    title: 'Execution Agent',
    desc: 'Submits transactions step-by-step with idempotency and retries.',
    bullets: ['orderId + stepId idempotency', 'Safe caps + deadlines', 'Wallet signatures only when required'],
  },
  {
    title: 'Monitor Agent',
    desc: 'Observes events and balances to compute final delivery and risk.',
    bullets: ['Bridge events + vault events', 'actualOut + Δ supplement', 'Circuit breakers and refund paths'],
  },
  {
    title: 'Accounting Agent',
    desc: 'Produces an auditable breakdown of costs and outcomes.',
    bullets: ['Bridge fee / gas / platform fee', 'Supplement and overflow handling', 'Receipts and reporting'],
  },
]

type DeliveryPhase = {
  title: string
  items: string[]
}

const phases: DeliveryPhase[] = [
  {
    title: 'Phase 1: MVP closure',
    items: ['402 → negotiate → commit → execute → receipt', 'Exact target amount semantics', 'Visible fee caps and deadlines'],
  },
  {
    title: 'Phase 2: Observable settlement',
    items: ['Monitors for bridge + vault events', 'Retry-safe execution and timeouts', 'Refund / rollback paths'],
  },
  {
    title: 'Phase 3: Pools and incentives',
    items: ['Multi-chain liquidity vaults', 'Risk-limited supplementation', 'LP accounting and rewards'],
  },
]

export default function AgentsPlan() {
  return (
    <section id="agents" className="bg-bg">
      <div className="container-x py-14 md:py-18">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Agents</h2>
            <p className="mt-2 max-w-3xl text-white/65">
              We separate “reasoning” from “execution”. Agents are deterministic, idempotent, and observable—so partners can audit outcomes
              and users can trust the promise.
            </p>
          </div>
          <a
            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10 hover:text-white"
            href="#roadmap"
          >
            View roadmap
          </a>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {agents.map((a) => (
            <AgentCard key={a.title} agent={a} />
          ))}
        </div>

        <div className="mt-10 glass2 p-7">
          <div className="text-base font-semibold text-white/90">Delivery plan</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {phases.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-sm font-semibold text-white/85">{p.title}</div>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
                  {p.items.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function AgentCard(props: { agent: Agent }) {
  const { agent } = props
  return (
    <div className="glass p-7">
      <div className="text-base font-semibold text-white/90">{agent.title}</div>
      <div className="mt-2 text-sm leading-relaxed text-white/65">{agent.desc}</div>
      <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-white/65">
        {agent.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
    </div>
  )
}
